import { useEffect, useState } from 'react';

const CRESTA_WIDGET_HOST_SELECTOR = '.cresta-widget-host';
const CRESTA_SCRIPT_ATTR = 'data-cresta-init';

// URL of the Cresta Dialogflow Messenger SDK module.
export const CRESTA_SDK_SRC = 'https://va-widget.us-west-2-prod.cresta.ai/chatgpt-widget-v2/index.js';

const CRESTA_STYLES = `
  df-messenger {
    --df-messenger-window-font-family: Arial;
    --df-messenger-window-font-size: large;
    --df-messenger-chat-background-color: #ffffff;
    --df-messenger-user-message-background-color: #0A2F54;
    --df-messenger-user-message-text-color: #ffffff;
    --ai-message-background-color: #EEF3F9;
    --ai-message-text-color: #0A2F54;
    --df-messenger-button-titlebar-color: #ffffff;
    --df-messenger-button-titlebar-font-color: #0A2F54;
    --df-messenger-button-titlebar-subtitle-color: #474F57;
    --df-messenger-form-button-background-color: #0A2F54;
    --df-messenger-form-button-text-color: #ffffff;
    --df-messenger-article-source-button-color: rgba(77, 156, 194, 1);
    --df-messenger-article-source-button-hover-color: #0A2F54;
    --df-messenger-input-border-color: #0A2F54;
    --df-messenger-send-icon-active-color: #0A2F54;
    --df-messenger-send-icon-color: #69727B;
    --df-messenger-send-icon-background-active-color: #0A2F54;
    --df-messenger-send-icon-background-color: #F0F4F7;
    --df-messenger-button-background-default: #61C6F5;
  }
`;

function injectCrestaWidget(): void {
	// Styles — assigned via .textContent so they are treated as a plain string, not markup.
	const styleEl = document.createElement('style');
	styleEl.setAttribute(CRESTA_SCRIPT_ATTR, '');
	styleEl.textContent = CRESTA_STYLES;
	document.head.appendChild(styleEl);

	// df-messenger custom element — the SDK will upgrade it once the module loads.
	const dfMessenger = document.createElement('df-messenger');
	dfMessenger.setAttribute('chat-title', 'How can I help?');
	dfMessenger.setAttribute('chat-subtitle', 'ClearChoice Concierge');
	dfMessenger.setAttribute(
		'chat-icon-mobile',
		'https://cdn.bfldr.com/LP3GHVAP/as/r6x357s6wz3kngwfg685hs/LG_CC_LauncherIcon_20260209?auto=webp&format=png'
	);
	dfMessenger.setAttribute(
		'chat-icon',
		'https://cdn.bfldr.com/LP3GHVAP/as/r6x357s6wz3kngwfg685hs/LG_CC_LauncherIcon_20260209?auto=webp&format=png'
	);
	dfMessenger.setAttribute(
		'header-icon',
		'https://cdn.bfldr.com/LP3GHVAP/as/74g5gqvqvwpk38rjkrwhm6s/LG_CC_HeaderIcon_20260209?auto=webp&format=png'
	);
	dfMessenger.setAttribute('ai-agent-name', 'ClearChoice Concierge');
	dfMessenger.setAttribute('launcher-visibility', 'hybrid');
	dfMessenger.setAttribute('ui-roundness', 'rectangle');
	dfMessenger.setAttribute('selected-theme', 'minimalist');
	dfMessenger.setAttribute('auth-client-id', 'clearchoice-va-prod');
	dfMessenger.setAttribute('recaptcha-site-key', '6LfEm-orAAAAAG2Woj73_FauW8Q-WhFDPbznYYj_');
	dfMessenger.setAttribute('recaptcha-checkbox-site-key', '6LeQouorAAAAAAvUGaNFHmJeynPZC13J8fk1bHxS');
	dfMessenger.setAttribute('crestagpt-customer', 'clearchoice');
	dfMessenger.setAttribute('crestagpt-profile', 'us-east-1');
	dfMessenger.setAttribute('crestagpt-usecase', 'clearchoice-chat-aia');
	dfMessenger.setAttribute(
		'crestagpt-agent',
		'gh-clearchoice-chat-7cb83e262406dea36432b7aa021694f2ce9863a1628d2a5ed364c934ec6b2cea'
	);
	dfMessenger.setAttribute('crestagpt-api', 'https://api-clearchoice-us-east-1.cresta.com');
	dfMessenger.setAttribute('crestagpt-auth-endpoint', 'https://auth.us-east-1-prod.cresta.com');
	document.body.appendChild(dfMessenger);

	// Cresta SDK — type="module" defers execution until after the document is parsed,
	// which keeps it off the critical rendering path.
	const sdkScript = document.createElement('script');
	sdkScript.type = 'module';
	sdkScript.src = CRESTA_SDK_SRC;
	document.body.appendChild(sdkScript);
}

/**
 * Traverses the Cresta widget shadow DOM and programmatically clicks the launcher button.
 * Must only be called after the initialization script has loaded (i.e. when `ready` is true).
 *
 * The widget host may live either as a direct child of <body> or inside <df-messenger>'s
 * own shadow root, depending on how the Cresta SDK structures the DOM. Both paths are checked.
 */
export function triggerCrestaChat(): void {
	const dfMessenger = document.querySelector('df-messenger');
	// .cresta-widget-host can be in the light DOM or inside df-messenger's shadow root.
	const widgetHost =
		document.querySelector(CRESTA_WIDGET_HOST_SELECTOR) ??
		dfMessenger?.shadowRoot?.querySelector(CRESTA_WIDGET_HOST_SELECTOR) ??
		null;

	if (widgetHost?.shadowRoot) {
		const launcher = widgetHost.shadowRoot.querySelector<HTMLElement>('#df-messenger-chatbot-button');
		launcher?.click();
	} else {
		console.warn('[Cresta] Widget host or shadow root not found — cannot open chat');
	}
}

/**
 * Injects the Cresta widget elements into the DOM when `enabled` is true, then watches
 * for the SDK to finish loading by observing when `.cresta-widget-host` appears.
 *
 * - Injection is idempotent: if a second ContentfulButton with launchCrestaAgent appears
 *   on the same page, the widget is not re-injected.
 * - Widget elements are intentionally never removed on unmount — they must persist for
 *   the lifetime of the page session.
 */
export function useCrestaAgent(enabled: boolean): { ready: boolean } {
	const [ready, setReady] = useState(false);

	useEffect(() => {
		if (!enabled) return;

		// The widget host may live in the light DOM or inside df-messenger's own shadow root.
		const isWidgetHostReady = (): boolean => {
			const dfMessenger = document.querySelector('df-messenger');
			return Boolean(
				document.querySelector(CRESTA_WIDGET_HOST_SELECTOR) ??
					dfMessenger?.shadowRoot?.querySelector(CRESTA_WIDGET_HOST_SELECTOR)
			);
		};

		// Idempotency: widget already injected by an earlier render.
		if (document.querySelector(`style[${CRESTA_SCRIPT_ATTR}]`)) {
			// Widget may already be fully initialized — check before starting an observer.
			if (isWidgetHostReady()) {
				setReady(true);
				return;
			}
		} else {
			injectCrestaWidget();
		}

		// The Cresta SDK module loads asynchronously. Use a MutationObserver to detect
		// when the SDK upgrades the custom element and creates the widget host.
		const observer = new MutationObserver(() => {
			if (isWidgetHostReady()) {
				setReady(true);
				observer.disconnect();
			}
		});

		observer.observe(document.body, { childList: true, subtree: true });

		return () => observer.disconnect();
	}, [enabled]);

	return { ready };
}
